import { ChevronLeft, ChevronRight } from 'lucide-react';
import { PAGE_SIZES, type PageSize } from '../../tournamentFilter';

/**
 * The table's footer: how much is shown, how much to show, and how to move between pages. The Tournaments
 * list and the Players screen both use it; a list with a fixed page size passes no `onPageSize`, and the
 * rows-per-page selector is left out.
 *
 * The range is stated in row numbers rather than page numbers because that is what an operator
 * is actually looking for when they scroll back to find something. The previous/next group
 * hides when everything fits — a pager that only ever says "1 of 1" is furniture — but the
 * footer itself stays, because rows-per-page lives here and a selector that vanished as soon
 * as the rows fit would be impossible to turn back down.
 */
export function Pager({
  page,
  pageSize,
  total,
  onPage,
  onPageSize,
  label = 'Tournament pages',
}: {
  page: number;
  pageSize: number;
  total: number;
  onPage: (page: number) => void;
  onPageSize?: (pageSize: PageSize) => void;
  /** What the pages are of, for the navigation landmark a screen reader announces. */
  label?: string;
}) {
  if (total === 0) return null;

  const pages = Math.max(1, Math.ceil(total / pageSize));
  const first = (page - 1) * pageSize + 1;
  const last = Math.min(page * pageSize, total);

  const button =
    'flex items-center gap-1 border border-ink-800/15 px-2.5 py-1.5 text-xs text-ink-800 transition hover:bg-felt-50 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent';

  return (
    <nav
      className="flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-ink-800/10 px-6 py-3 print:hidden"
      aria-label={label}
    >
      <span className="num text-xs text-ink-800/60">
        {first.toLocaleString()}–{last.toLocaleString()} of {total.toLocaleString()}
      </span>

      {onPageSize ? (
        <label className="flex items-center gap-2 text-xs text-ink-800/60">
          Rows per page
          <select
            value={pageSize}
            onChange={(e) => onPageSize(Number(e.target.value) as PageSize)}
            className="border border-ink-800/15 bg-white px-2 py-1 text-xs text-ink-950 outline-none focus:border-felt-500"
          >
            {PAGE_SIZES.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </label>
      ) : null}

      {pages > 1 ? (
        <div className="ml-auto flex items-center gap-2">
          <button type="button" onClick={() => onPage(page - 1)} disabled={page <= 1} className={button}>
            <ChevronLeft className="size-3.5" /> Previous
          </button>
          <span className="num px-1 text-xs text-ink-800/60">
            Page {page.toLocaleString()} of {pages.toLocaleString()}
          </span>
          <button
            type="button"
            onClick={() => onPage(page + 1)}
            disabled={page >= pages}
            className={button}
          >
            Next <ChevronRight className="size-3.5" />
          </button>
        </div>
      ) : null}
    </nav>
  );
}
